"use client"

import { useState, useEffect } from "react"
import { apiService } from "../../services/apiService"
import "./AdminDashboard.css"

const PrayerRequests = () => {
  const [prayerRequests, setPrayerRequests] = useState([])
  const [selectedRequest, setSelectedRequest] = useState(null)
  const [filterStatus, setFilterStatus] = useState("all")
  const [filterPrivacy, setFilterPrivacy] = useState("all")
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState("")
  const [successMessage, setSuccessMessage] = useState("")

  useEffect(() => {
    loadPrayerRequests()
  }, [])

  // Clear success message after 5 seconds
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => {
        setSuccessMessage("")
      }, 5000)
      return () => clearTimeout(timer)
    }
  }, [successMessage])

  const loadPrayerRequests = async () => {
    try {
      setIsLoading(true)
      setError("")
      const response = await apiService.getPrayerRequests()
      // Sort prayer requests by creation date in descending order (newest first)
      const sortedRequests = response.sort((a, b) => 
        new Date(b.created_at) - new Date(a.created_at)
      )
      setPrayerRequests(sortedRequests)
    } catch (error) {
      console.error('Error loading prayer requests:', error)
      setError(error.message || 'Failed to load prayer requests')
    } finally {
      setIsLoading(false)
    }
  }

  const handleStatusFilterChange = (e) => {
    setFilterStatus(e.target.value)
  }

  const handlePrivacyFilterChange = (e) => {
    setFilterPrivacy(e.target.value)
  }

  const updateRequestStatus = async (id, newStatus) => {
    try {
      setIsLoading(true)
      setError("")
      await apiService.updatePrayerRequestStatus(id, newStatus)
      setSuccessMessage("Prayer request status updated successfully!")
      loadPrayerRequests()
    } catch (error) {
      console.error('Error updating prayer request status:', error)
      setError(error.message || 'Failed to update prayer request status')
    } finally {
      setIsLoading(false)
    }
  }

  const deleteRequest = async (id) => {
    if (window.confirm("Are you sure you want to delete this prayer request?")) {
      try {
        setIsLoading(true)
        setError("")
        await apiService.deletePrayerRequest(id)
        setSuccessMessage("Prayer request deleted successfully!")
        loadPrayerRequests()
      } catch (error) {
        console.error('Error deleting prayer request:', error)
        setError(error.message || 'Failed to delete prayer request')
      } finally {
        setIsLoading(false)
      }
    }
  }

  const viewRequestDetails = async (id) => {
    try {
      setIsLoading(true)
      setError("")
      const request = await apiService.getPrayerRequestById(id)
      setSelectedRequest(request)
    } catch (error) {
      console.error('Error fetching prayer request details:', error)
      setError(error.message || 'Failed to fetch prayer request details')
    } finally {
      setIsLoading(false)
    }
  }

  const filteredRequests = prayerRequests.filter((request) => {
    const matchesStatus = filterStatus === "all" || request.status === filterStatus
    const matchesPrivacy =
      filterPrivacy === "all" ||
      (filterPrivacy === "public" && !request.is_private) ||
      (filterPrivacy === "private" && request.is_private)
    return matchesStatus && matchesPrivacy
  })

  return (
    <div className="prayer-requests-page">
      {error && <div className="error-message">{error}</div>}
      {successMessage && <div className="success-message">{successMessage}</div>}

      <div className="filters-container">
        <div className="filter-box">
          <select value={filterStatus} onChange={handleStatusFilterChange} className="form-control">
            <option value="all">All Statuses</option>
            <option value="new">New</option>
            <option value="praying">Praying</option>
            <option value="answered">Answered</option>
          </select>
        </div>

        <div className="filter-box">
          <select value={filterPrivacy} onChange={handlePrivacyFilterChange} className="form-control">
            <option value="all">All Requests</option>
            <option value="public">Public</option>
            <option value="private">Private</option>
          </select>
        </div>
      </div>

      <h2 className="section-title">Prayer Requests</h2>

      {isLoading && !selectedRequest ? (
        <div className="loading">Loading prayer requests...</div>
      ) : selectedRequest ? (
        <div className="prayer-request-details">
          <button className="btn btn-secondary" onClick={() => setSelectedRequest(null)}>
            <i className="material-icons">arrow_back</i> Back to List
          </button>
          
          <div className="prayer-request-card card">
            <div className="prayer-request-header">
              <div className="prayer-request-info">
                <h3 className="prayer-request-name">{selectedRequest.name}</h3>
                <p className="prayer-request-date">{new Date(selectedRequest.created_at).toLocaleDateString()}</p>
              </div>
              <div className="prayer-request-badges">
                <span className={`status-badge status-${selectedRequest.status}`}>
                  {selectedRequest.status === "new" ? "New" : selectedRequest.status === "praying" ? "Praying" : "Answered"}
                </span>
                {selectedRequest.is_private && (
                  <span className="privacy-badge">
                    <i className="material-icons">lock</i> Private
                  </span>
                )}
              </div>
            </div>

            <div className="prayer-request-content">
              <div className="detail-group">
                <label>Email:</label>
                <p>{selectedRequest.email}</p>
              </div>
              <div className="detail-group">
                <label>Phone:</label>
                <p>{selectedRequest.phone}</p>
              </div>
              <div className="detail-group">
                <label>Country:</label>
                <p>{selectedRequest.country}</p>
              </div>
              <div className="detail-group">
                <label>Request:</label>
                <p>{selectedRequest.description}</p>
              </div>
            </div>

            <div className="prayer-request-actions">
              {selectedRequest.status === "new" && (
                <button className="btn btn-sm" onClick={() => updateRequestStatus(selectedRequest.id, "praying")}>
                  <i className="material-icons">favorite</i> Mark as Praying
                </button>
              )}
              {selectedRequest.status === "praying" && (
                <button className="btn btn-sm" onClick={() => updateRequestStatus(selectedRequest.id, "answered")}>
                  <i className="material-icons">check_circle</i> Mark as Answered
                </button>
              )}
              <button className="btn btn-sm btn-secondary" onClick={() => deleteRequest(selectedRequest.id)}>
                <i className="material-icons">delete</i> Delete
              </button>
            </div>
          </div>
        </div>
      ) : filteredRequests.length > 0 ? (
        filteredRequests.map((request) => (
          <div className="prayer-request-card card" key={request.id}>
            <div className="prayer-request-header">
              <div className="prayer-request-info">
                <h3 className="prayer-request-name">{request.name}</h3>
                <p className="prayer-request-date">{new Date(request.created_at).toLocaleDateString()}</p>
              </div>
              <div className="prayer-request-badges">
                <span className={`status-badge status-${request.status}`}>
                  {request.status === "new" ? "New" : request.status === "praying" ? "Praying" : "Answered"}
                </span>
                {request.is_private && (
                  <span className="privacy-badge">
                    <i className="material-icons">lock</i> Private
                  </span>
                )}
              </div>
            </div>

            <div className="prayer-request-content">
              <p>{request.description}</p>
            </div>

            <div className="prayer-request-actions">
              <button className="btn btn-sm" onClick={() => viewRequestDetails(request.id)}>
                <i className="material-icons">visibility</i> View Details
              </button>
              {request.status === "new" && (
                <button className="btn btn-sm" onClick={() => updateRequestStatus(request.id, "praying")}>
                  <i className="material-icons">favorite</i> Mark as Praying
                </button>
              )}
              {request.status === "praying" && (
                <button className="btn btn-sm" onClick={() => updateRequestStatus(request.id, "answered")}>
                  <i className="material-icons">check_circle</i> Mark as Answered
                </button>
              )}
              <button className="btn btn-sm btn-secondary" onClick={() => deleteRequest(request.id)}>
                <i className="material-icons">delete</i> Delete
              </button>
            </div>
          </div>
        ))
      ) : (
        <div className="no-results">
          <p>No prayer requests found matching your filter criteria.</p>
        </div>
      )}
    </div>
  )
}

export default PrayerRequests
